import React from "react";
import { useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";

import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem,
  Divider,
  Checkbox,
  Drawer,
  Skeleton,
  Tooltip,
} from "@mui/material";

import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  DeleteOutline as DeleteOutlineIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  UnfoldMore as UnfoldMoreIcon,
  AccountBoxOutlined as AccountBoxOutlinedIcon,
  Settings as SettingsIcon,
  PeopleOutline as PeopleOutlineIcon,
  EditOutlined as EditOutlinedIcon,
  Apartment as ApartmentIcon,
  HourglassEmpty as HourglassEmptyIcon,
  WarningAmberOutlined as WarningAmberOutlinedIcon,
  SettingsBackupRestore as RestoreIcon,
} from "@mui/icons-material";

import DeleteOrganisationModal from "./M_DeleteOrganisation";
import RestoreOrganisationModal from "./M_RestoreOrganisation";
import EditOrganisationModal from "./M_EditOrganisation";

import useOrganisation from "../../../../hooks/super/useOrganisation";
import useAuth from "../../../../hooks/useAuth";
import { formatDate } from "../../../../utils/format";
import { UserType } from "../../../../utils/constants";
import { setOrganisationSession, setOrganisationName, setOrganisationAdmin } from "../../../../utils/jwt";

// =====================================================================
// Design tokens
// =====================================================================
const TOKENS = {
  primary: "#6371F7",
  primarySoft: "#EEF0FE",
  text: "#1A1B23",
  textSoft: "#5B5C66",
  textMuted: "#8A8C99",
  border: "#E6E7EC",
  borderSoft: "#F1F2F6",
  surface: "#FFFFFF",
  surfaceAlt: "#FAFBFD",
  shadow: "0 1px 2px rgba(16,24,40,0.05)",
  shadowSelected: "0 2px 12px rgba(99,113,247,0.18)",
  radius: 12,
  radiusLg: 16,
  plans: {
    Community: { ink: "#7B7B7B", bg: "#F4F4F4", border: "#E5E5E5", rank: 0, seats: 1 },
    Pro: { ink: "#0255ED", bg: "#EDF3FE", border: "#C7DAFB", rank: 1, seats: 10 },
    Business: { ink: "#916C19", bg: "#FFF6D0", border: "#FFE89A", rank: 2, seats: 50 },
    Enterprise: { ink: "#454FAD", bg: "#EBEDF5", border: "#CED2F2", rank: 3, seats: 999 },
  },
  statuses: {
    Active: { ink: "#0f7a42", bg: "#E8FBEF", border: "#B8ECD0", dot: "#1FC16B" },
    Trial: { ink: "#0255ED", bg: "#EDF3FE", border: "#C7DAFB", dot: "#3577F1" },
    Pending: { ink: "#7B7B7B", bg: "#F4F4F4", border: "#E0E0E0", dot: "#B0B0B0" },
    Suspended: { ink: "#7a5b10", bg: "#FFF6D0", border: "#FFE89A", dot: "#F2B429" },
    Expired: { ink: "#A11228", bg: "#FEECEE", border: "#F7C9CF", dot: "#FB3748" },
  },
  monoPalette: [
    { bg: "#EEF0FE", ink: "#454FAD" }, // indigo
    { bg: "#EDF3FE", ink: "#0255ED" }, // blue
    { bg: "#E8FBEF", ink: "#0f7a42" }, // green
    { bg: "#FFF6D0", ink: "#916C19" }, // amber
    { bg: "#FEECEE", ink: "#A11228" }, // red
    { bg: "#F2EAFB", ink: "#6B2EBB" }, // purple
    { bg: "#E1F6F5", ink: "#0F6E68" }, // teal
    { bg: "#FFE9D4", ink: "#A14A0F" }, // orange
  ],
};

const PLAN_LABEL_BY_LICENSE = ["Community", "Pro", "Business", "Enterprise"];

// =====================================================================
// Helpers
// =====================================================================
const planForLicense = (license) => {
  const idx = Number.isInteger(license) ? license : 0;
  return PLAN_LABEL_BY_LICENSE[idx] || "Community";
};

const hashCode = (str) => {
  let h = 0;
  const s = String(str || "");
  for (let i = 0; i < s.length; i++) {
    h = (h << 5) - h + s.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
};

const initialsOf = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) return "??";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const monoSwatch = (name) => TOKENS.monoPalette[hashCode(name) % TOKENS.monoPalette.length];

const daysBetween = (a, b) => Math.floor((a - b) / (1000 * 60 * 60 * 24));

const relativeFromNow = (date) => {
  if (!date) return "";
  const d = new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const diff = daysBetween(d, now);
  if (diff === 0) return "today";
  if (diff > 0) {
    if (diff === 1) return "in 1 day";
    if (diff < 30) return `in ${diff} days`;
    if (diff < 365) return `in ${Math.round(diff / 30)} mo`;
    return `in ${Math.round(diff / 365)} yr`;
  }
  const ago = -diff;
  if (ago === 1) return "1 day ago";
  if (ago < 30) return `${ago} days ago`;
  if (ago < 365) return `${Math.round(ago / 30)} mo ago`;
  return `${Math.round(ago / 365)} yr ago`;
};

const deriveStatus = (org) => {
  if (org?.isDeleted) return "Suspended";
  const expiry = org?.current_period_end ? new Date(org.current_period_end) : null;
  const now = new Date();
  if (expiry && !Number.isNaN(expiry.getTime())) {
    if (expiry < now) return "Expired";
    const days = daysBetween(expiry, now);
    if (days < 14 && (org?.license || 0) === 0) return "Trial";
  }
  const sites = Number(org?.sites || 0);
  const users = Number(org?.users || 0);
  if (sites === 0 && users <= 1 && (org?.license || 0) === 0) return "Pending";
  return "Active";
};

// =====================================================================
// Presentational primitives
// =====================================================================
const Monogram = ({ name, size = 40 }) => {
  const { bg, ink } = monoSwatch(name);
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flex: "0 0 auto",
        borderRadius: "10px",
        background: bg,
        color: ink,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: size >= 40 ? 14 : 12,
        letterSpacing: 0.4,
      }}
    >
      {initialsOf(name)}
    </Box>
  );
};

const Chip = ({ ink, bg, border, children, dot, sx }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.25,
      py: 0.35,
      borderRadius: "999px",
      background: bg,
      color: ink,
      border: `1px solid ${border}`,
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1.4,
      whiteSpace: "nowrap",
      ...sx,
    }}
  >
    {dot ? (
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: dot }} />
    ) : null}
    {children}
  </Box>
);

const PlanChip = ({ plan }) => {
  const t = TOKENS.plans[plan] || TOKENS.plans.Community;
  return (
    <Chip ink={t.ink} bg={t.bg} border={t.border}>
      {plan}
    </Chip>
  );
};

const StatusPill = ({ status }) => {
  const t = TOKENS.statuses[status] || TOKENS.statuses.Pending;
  return (
    <Chip ink={t.ink} bg={t.bg} border={t.border} dot={t.dot}>
      {status}
    </Chip>
  );
};

const UnassignedChip = () => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "8px",
        border: `1px dashed ${TOKENS.border}`,
        color: TOKENS.textMuted,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <AccountBoxOutlinedIcon sx={{ fontSize: 14 }} />
    </Box>
    <Typography
      sx={{ fontStyle: "italic", color: TOKENS.textMuted, fontSize: 13 }}
    >
      Unassigned
    </Typography>
  </Stack>
);

const UsageBar = ({ users, plan }) => {
  const seats = TOKENS.plans[plan]?.seats ?? 1;
  const unlimited = plan === "Enterprise";
  const ratio = unlimited ? Math.min(users / 100, 1) : Math.min(users / Math.max(seats, 1), 1);
  const pct = Math.round(ratio * 100);
  let color = "#1FC16B";
  if (unlimited) color = TOKENS.primary;
  else if (pct >= 85) color = "#FB3748";
  else if (pct >= 60) color = "#F2B429";
  return (
    <Box sx={{ minWidth: 110 }}>
      <Typography sx={{ fontSize: 11, color: TOKENS.textSoft, mb: 0.25 }}>
        {unlimited ? `${users} / unlimited` : `${users} / ${seats} (${pct}%)`}
      </Typography>
      <Box
        sx={{
          height: 6,
          width: "100%",
          background: TOKENS.borderSoft,
          borderRadius: 999,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: `${unlimited ? Math.max(8, pct) : Math.max(2, pct)}%`,
            background: color,
            borderRadius: 999,
            transition: "width .25s ease",
          }}
        />
      </Box>
    </Box>
  );
};

const ExpiryCell = ({ value }) => {
  if (!value) return <Typography sx={{ color: TOKENS.textMuted, fontSize: 13 }}>—</Typography>;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return <Typography sx={{ color: TOKENS.textMuted }}>—</Typography>;
  const now = new Date();
  const past = d < now;
  const soon = !past && daysBetween(d, now) < 14;
  const color = past ? "#A11228" : soon ? "#7a5b10" : TOKENS.text;
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ fontSize: 13, color, fontWeight: past || soon ? 600 : 500 }}>
        {formatDate(value)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: TOKENS.textMuted }}>
        {relativeFromNow(value)}
      </Typography>
    </Stack>
  );
};

const CreatedCell = ({ value }) => {
  if (!value) return <Typography sx={{ color: TOKENS.textMuted }}>—</Typography>;
  return (
    <Stack spacing={0.25}>
      <Typography sx={{ fontSize: 13, color: TOKENS.text, fontWeight: 500 }}>
        {formatDate(value)}
      </Typography>
      <Typography sx={{ fontSize: 11, color: TOKENS.textMuted }}>
        {relativeFromNow(value)}
      </Typography>
    </Stack>
  );
};

// =====================================================================
// Stat tile
// =====================================================================
const StatTile = ({ label, value, Icon, accent, selected, onClick }) => (
  <Box
    onClick={onClick}
    sx={{
      cursor: onClick ? "pointer" : "default",
      background: TOKENS.surface,
      border: `1px solid ${selected ? TOKENS.primary : TOKENS.border}`,
      borderRadius: `${TOKENS.radiusLg}px`,
      padding: "16px 18px",
      boxShadow: selected ? TOKENS.shadowSelected : TOKENS.shadow,
      transition: "all .15s ease",
      "&:hover": onClick ? { borderColor: TOKENS.primary, boxShadow: TOKENS.shadowSelected } : {},
    }}
  >
    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
      <Typography sx={{ fontSize: 12, color: TOKENS.textSoft, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
        {label}
      </Typography>
      {Icon ? (
        <Box
          sx={{
            width: 30,
            height: 30,
            borderRadius: "10px",
            background: accent?.bg || TOKENS.primarySoft,
            color: accent?.ink || TOKENS.primary,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon sx={{ fontSize: 16 }} />
        </Box>
      ) : null}
    </Stack>
    <Typography sx={{ fontSize: 26, fontWeight: 700, color: TOKENS.text, lineHeight: 1.1 }}>
      {value}
    </Typography>
  </Box>
);

// =====================================================================
// Sort header
// =====================================================================
const SortHeader = ({ id, label, orderBy, order, onSort, align = "left" }) => {
  const active = orderBy === id;
  const Icon = active ? (order === "asc" ? KeyboardArrowUpIcon : KeyboardArrowDownIcon) : UnfoldMoreIcon;
  return (
    <Box
      onClick={() => onSort(id)}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: "pointer",
        userSelect: "none",
        color: active ? TOKENS.text : TOKENS.textSoft,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.4,
        textTransform: "uppercase",
        justifyContent: align === "right" ? "flex-end" : "flex-start",
        width: align === "right" ? "100%" : "auto",
      }}
    >
      {label}
      <Icon sx={{ fontSize: 14, opacity: active ? 0.9 : 0.5 }} />
    </Box>
  );
};

// =====================================================================
// Row (table)
// =====================================================================
const HeaderCell = styled("th")(({ align = "left", compact }) => ({
  textAlign: align,
  padding: compact ? "8px 10px" : "12px 14px",
  borderBottom: `1px solid ${TOKENS.border}`,
  background: TOKENS.surfaceAlt,
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
  zIndex: 1,
}));

const DataCell = styled("td")(({ align = "left", compact }) => ({
  textAlign: align,
  padding: compact ? "8px 10px" : "14px 14px",
  borderBottom: `1px solid ${TOKENS.borderSoft}`,
  verticalAlign: "middle",
  color: TOKENS.text,
  fontSize: 13,
}));

// =====================================================================
// Main component
// =====================================================================
const PAGE_SIZE = 20;

function T_Organisation() {
  const navigate = useNavigate();
  const {
    organisations,
    total_organisation_count,
    from: contextFrom,
    getOrganisations,
    deleteOrganisation,
  } = useOrganisation();
  const { adminUserType, setUserType, setUserLicense, getFeatures } = useAuth();
  const canManage = [UserType.SUPER_ADMIN, UserType.ADMIN].includes(adminUserType);

  // ---- local UI state
  const [search, setSearch] = React.useState("");
  const [planFilter, setPlanFilter] = React.useState([]); // multi
  const [statusFilter, setStatusFilter] = React.useState([]); // multi
  const [orderBy, setOrderBy] = React.useState("created");
  const [order, setOrder] = React.useState("desc");
  const [selected, setSelected] = React.useState([]);
  const [view, setView] = React.useState("table"); // 'table' | 'cards'
  const [density, setDensity] = React.useState("comfortable"); // 'comfortable' | 'compact'

  // ---- infinite scroll state
  // Context replaces `organisations` on every fetch, so we mirror successive
  // batches into a local accumulator. `loadedCount` is the source of truth
  // for the next `from` cursor and the end-of-list affordance.
  const [accumulated, setAccumulated] = React.useState([]);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasFetchedFirst, setHasFetchedFirst] = React.useState(false);
  const sentinelRef = React.useRef(null);
  const observerRef = React.useRef(null);

  // ---- modal state
  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [restoreOpen, setRestoreOpen] = React.useState(false);
  const [editOpen, setEditOpen] = React.useState(false);
  const [removeFlag, setRemoveFlag] = React.useState(false);
  const [actionOrgs, setActionOrgs] = React.useState([]); // [{id,title}] for modal payloads
  const [actionIds, setActionIds] = React.useState([]); // ids array or scalar

  // ---- kebab menu state
  const [menuAnchor, setMenuAnchor] = React.useState(null);
  const [menuOrg, setMenuOrg] = React.useState(null);

  // ---- detail drawer
  const [drawerOrg, setDrawerOrg] = React.useState(null);

  // ---- plan/status filter menus
  const [planMenuAnchor, setPlanMenuAnchor] = React.useState(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = React.useState(null);

  // Kick off the first page once on mount.
  React.useEffect(() => {
    if (!hasFetchedFirst && typeof getOrganisations === "function") {
      setHasFetchedFirst(true);
      setLoadingMore(true);
      getOrganisations(PAGE_SIZE, 0);
    }
  }, [hasFetchedFirst, getOrganisations]);

  // Whenever the context delivers a fresh batch, fold it into `accumulated`.
  // When the cursor is at 0 the batch is the first page (initial load, Refresh,
  // or a post-mutation refetch — delete/restore/edit/create all fire
  // `getOrganisations(rows, 0)` inside the context). In that case REPLACE the
  // accumulator so rows that disappeared from the server (e.g. a just-deleted
  // org) are dropped from the table — otherwise the stale id would linger
  // because dedupe-then-append never removes anything. For any from > 0 batch
  // we dedupe-append as before.
  React.useEffect(() => {
    if (!Array.isArray(organisations)) return;
    if (contextFrom === 0) {
      setAccumulated(organisations);
      // Drop any selections whose row was just removed by the refetch.
      setSelected((cur) => {
        if (cur.length === 0) return cur;
        const present = new Set(organisations.map((o) => o.id));
        const next = cur.filter((id) => present.has(id));
        return next.length === cur.length ? cur : next;
      });
    } else {
      setAccumulated((cur) => {
        const seen = new Set(cur.map((o) => o.id));
        const additions = organisations.filter((o) => !seen.has(o.id));
        if (additions.length === 0) return cur;
        return [...cur, ...additions];
      });
    }
    setLoadingMore(false);
  }, [organisations, contextFrom]);

  // ---- derived data
  const enriched = React.useMemo(() => {
    if (!Array.isArray(accumulated)) return [];
    return accumulated.map((o) => ({
      ...o,
      _plan: planForLicense(o.license),
      _status: deriveStatus(o),
    }));
  }, [accumulated]);

  const filtered = React.useMemo(() => {
    let list = enriched;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(
        (o) =>
          String(o.title || "").toLowerCase().includes(q) ||
          String(o.id || "").toLowerCase().includes(q) ||
          String(o._plan || "").toLowerCase().includes(q)
      );
    }
    if (planFilter.length) list = list.filter((o) => planFilter.includes(o._plan));
    if (statusFilter.length) list = list.filter((o) => statusFilter.includes(o._status));
    return list;
  }, [enriched, search, planFilter, statusFilter]);

  const sorted = React.useMemo(() => {
    const list = [...filtered];
    const dir = order === "asc" ? 1 : -1;
    list.sort((a, b) => {
      let av = a[orderBy];
      let bv = b[orderBy];
      if (orderBy === "_plan") {
        av = TOKENS.plans[a._plan]?.rank ?? -1;
        bv = TOKENS.plans[b._plan]?.rank ?? -1;
      }
      if (orderBy === "_status") {
        av = a._status;
        bv = b._status;
      }
      if (orderBy === "created" || orderBy === "current_period_end") {
        av = av ? new Date(av).getTime() : 0;
        bv = bv ? new Date(bv).getTime() : 0;
      }
      if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv) * dir;
      if (av == null) return 1 * dir;
      if (bv == null) return -1 * dir;
      return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
    });
    return list;
  }, [filtered, orderBy, order]);

  // Infinite scroll: the table/cards render the full accumulated+filtered+sorted
  // list. No client pagination slice. (Newly-appended rows are added on top of
  // any existing selection; the selection state survives scroll naturally.)
  const visible = sorted;

  // ---- infinite-scroll loader
  const loadNextPage = React.useCallback(() => {
    if (loadingMore) return;
    if (organisations === null) return; // first fetch already in flight
    if (accumulated.length === 0) return; // wait for first batch
    if (
      total_organisation_count > 0 &&
      accumulated.length >= total_organisation_count
    )
      return;
    if (typeof getOrganisations !== "function") return;
    setLoadingMore(true);
    // init=false so the total count isn't reset to 0 between pages
    getOrganisations(PAGE_SIZE, accumulated.length, false);
  }, [
    loadingMore,
    organisations,
    accumulated.length,
    total_organisation_count,
    getOrganisations,
  ]);

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return undefined;
    // Re-create the observer when `loadNextPage` identity changes so the
    // closure captures the freshest `accumulated.length` cursor.
    if (observerRef.current) observerRef.current.disconnect();
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) loadNextPage();
      },
      { rootMargin: "200px" }
    );
    io.observe(node);
    observerRef.current = io;
    return () => {
      io.disconnect();
      observerRef.current = null;
    };
  }, [loadNextPage]);

  // ---- counts for stat tiles
  const counts = React.useMemo(() => {
    const c = { total: enriched.length, active: 0, trial: 0, attention: 0 };
    enriched.forEach((o) => {
      if (o._status === "Active") c.active += 1;
      else if (o._status === "Trial") c.trial += 1;
      else if (o._status === "Suspended" || o._status === "Expired") c.attention += 1;
    });
    return c;
  }, [enriched]);

  // ---- handlers
  const handleSort = (id) => {
    if (orderBy === id) setOrder(order === "asc" ? "desc" : "asc");
    else {
      setOrderBy(id);
      setOrder("asc");
    }
  };

  const togglePlanInMenu = (plan) => {
    setPlanFilter((cur) => (cur.includes(plan) ? cur.filter((p) => p !== plan) : [...cur, plan]));
  };

  const toggleStatusInMenu = (status) => {
    setStatusFilter((cur) =>
      cur.includes(status) ? cur.filter((s) => s !== status) : [...cur, status]
    );
  };

  const allOnPageSelected = visible.length > 0 && visible.every((o) => selected.includes(o.id));
  const someOnPageSelected = visible.some((o) => selected.includes(o.id)) && !allOnPageSelected;

  const handleSelectAllOnPage = (checked) => {
    if (checked) {
      const next = new Set(selected);
      visible.forEach((o) => next.add(o.id));
      setSelected(Array.from(next));
    } else {
      const ids = new Set(visible.map((o) => o.id));
      setSelected(selected.filter((id) => !ids.has(id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((cur) => (cur.includes(id) ? cur.filter((i) => i !== id) : [...cur, id]));
  };

  const refresh = () => {
    if (typeof getOrganisations !== "function") return;
    setAccumulated([]);
    setSelected([]);
    setLoadingMore(true);
    getOrganisations(PAGE_SIZE, 0);
  };

  // ---- bulk actions
  const orgsByIds = (ids) =>
    enriched
      .filter((o) => ids.includes(o.id))
      .map((o) => ({ id: o.id, title: o.title }));

  const bulkSuspend = async () => {
    if (!selected.length || !canManage) return;
    // soft delete
    for (const id of selected) {
      // eslint-disable-next-line no-await-in-loop
      await deleteOrganisation(id, false);
    }
    setSelected([]);
  };

  const bulkActivate = async () => {
    // No explicit "activate" mutation in context; surface via restore for soft-deleted rows
    if (!canManage) return;
    const restorable = selected.filter((id) => {
      const o = enriched.find((x) => x.id === id);
      return o?.isDeleted;
    });
    if (!restorable.length) return;
    setActionIds(restorable);
    setActionOrgs(orgsByIds(restorable));
    setRestoreOpen(true);
  };

  const bulkRemove = () => {
    if (!selected.length || !canManage) return;
    setActionIds(selected);
    setActionOrgs(orgsByIds(selected));
    setRemoveFlag(true);
    setDeleteOpen(true);
  };

  // ---- per-row actions
  const openKebab = (event, org) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuOrg(org);
  };
  const closeKebab = () => {
    setMenuAnchor(null);
    setMenuOrg(null);
  };

  const handleViewDetails = (org) => {
    setDrawerOrg(org);
  };

  const handleConfigure = async (org) => {
    if (!org) return;
    closeKebab();
    setOrganisationSession(org.id);
    setOrganisationName(org.title);
    setOrganisationAdmin(org.admin?.email);
    setUserType(UserType.ORG_ADMIN);
    setUserLicense(org.license);
    try {
      await getFeatures();
    } catch (e) {
      /* non-fatal */
    }
    navigate("/v2/overview");
  };

  const handleUsers = (org) => {
    if (!org) return;
    closeKebab();
    navigate("/super/application/user/list/" + org.id);
  };

  const handleEdit = (org) => {
    if (!org) return;
    closeKebab();
    setActionOrgs([{ id: org.id, title: org.title }]);
    setEditOpen(true);
  };

  const handleSuspend = async (org) => {
    if (!org) return;
    closeKebab();
    if (!canManage) return;
    // soft delete via removeFlag=false
    await deleteOrganisation(org.id, false);
  };

  const handleRemove = (org) => {
    if (!org) return;
    closeKebab();
    setActionIds(org.id);
    setActionOrgs([{ id: org.id, title: org.title }]);
    setRemoveFlag(true);
    setDeleteOpen(true);
  };

  const handleRestore = (org) => {
    if (!org) return;
    closeKebab();
    setActionIds(org.id);
    setActionOrgs([{ id: org.id, title: org.title }]);
    setRestoreOpen(true);
  };

  const loading = organisations === null && accumulated.length === 0;
  const compact = density === "compact";

  // =====================================================================
  // Render
  // =====================================================================
  return (
    <Stack spacing={3}>
      {/* Summary strip */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
          gap: 2,
        }}
      >
        <StatTile
          label="Total organisations"
          value={loading ? <Skeleton width={60} /> : counts.total}
          Icon={ApartmentIcon}
          accent={{ bg: TOKENS.primarySoft, ink: TOKENS.primary }}
        />
        <StatTile
          label="Active"
          value={loading ? <Skeleton width={60} /> : counts.active}
          Icon={CheckCircleOutlineIcon}
          accent={{ bg: TOKENS.statuses.Active.bg, ink: TOKENS.statuses.Active.ink }}
          selected={statusFilter.length === 1 && statusFilter[0] === "Active"}
          onClick={() => setStatusFilter((s) => (s.length === 1 && s[0] === "Active" ? [] : ["Active"]))}
        />
        <StatTile
          label="On trial"
          value={loading ? <Skeleton width={60} /> : counts.trial}
          Icon={HourglassEmptyIcon}
          accent={{ bg: TOKENS.statuses.Trial.bg, ink: TOKENS.statuses.Trial.ink }}
          selected={statusFilter.length === 1 && statusFilter[0] === "Trial"}
          onClick={() => setStatusFilter((s) => (s.length === 1 && s[0] === "Trial" ? [] : ["Trial"]))}
        />
        <StatTile
          label="Need attention"
          value={loading ? <Skeleton width={60} /> : counts.attention}
          Icon={WarningAmberOutlinedIcon}
          accent={{ bg: TOKENS.statuses.Expired.bg, ink: TOKENS.statuses.Expired.ink }}
          selected={
            statusFilter.length === 2 &&
            statusFilter.includes("Suspended") &&
            statusFilter.includes("Expired")
          }
          onClick={() =>
            setStatusFilter((s) => {
              const isPair = s.length === 2 && s.includes("Suspended") && s.includes("Expired");
              return isPair ? [] : ["Suspended", "Expired"];
            })
          }
        />
      </Box>

      {/* Main panel */}
      <Box
        sx={{
          background: TOKENS.surface,
          border: `1px solid ${TOKENS.border}`,
          borderRadius: `${TOKENS.radiusLg}px`,
          p: "18px",
          boxShadow: TOKENS.shadow,
        }}
      >
        {/* Toolbar */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" flex={1} flexWrap="wrap">
            <TextField
              size="small"
              placeholder="Search by name, id, plan…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: TOKENS.textMuted }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth: 260,
                "& .MuiOutlinedInput-root": { borderRadius: "10px" },
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setPlanMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: "none",
                borderColor: TOKENS.border,
                color: TOKENS.text,
                "&:hover": { borderColor: TOKENS.primary, background: TOKENS.primarySoft },
              }}
            >
              Plan{planFilter.length ? ` · ${planFilter.length}` : ""}
            </Button>
            <Menu
              anchorEl={planMenuAnchor}
              open={Boolean(planMenuAnchor)}
              onClose={() => setPlanMenuAnchor(null)}
            >
              {Object.keys(TOKENS.plans).map((plan) => (
                <MenuItem key={plan} onClick={() => togglePlanInMenu(plan)} dense>
                  <Checkbox size="small" checked={planFilter.includes(plan)} />
                  <PlanChip plan={plan} />
                </MenuItem>
              ))}
              {planFilter.length > 0 && [
                <Divider key="d" />,
                <MenuItem key="clr" onClick={() => setPlanFilter([])} dense>
                  Clear all
                </MenuItem>,
              ]}
            </Menu>
            <Button
              size="small"
              variant="outlined"
              startIcon={<FilterListIcon sx={{ fontSize: 16 }} />}
              endIcon={<KeyboardArrowDownIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setStatusMenuAnchor(e.currentTarget)}
              sx={{
                textTransform: "none",
                borderColor: TOKENS.border,
                color: TOKENS.text,
                "&:hover": { borderColor: TOKENS.primary, background: TOKENS.primarySoft },
              }}
            >
              Status{statusFilter.length ? ` · ${statusFilter.length}` : ""}
            </Button>
            <Menu
              anchorEl={statusMenuAnchor}
              open={Boolean(statusMenuAnchor)}
              onClose={() => setStatusMenuAnchor(null)}
            >
              {Object.keys(TOKENS.statuses).map((status) => (
                <MenuItem key={status} onClick={() => toggleStatusInMenu(status)} dense>
                  <Checkbox size="small" checked={statusFilter.includes(status)} />
                  <StatusPill status={status} />
                </MenuItem>
              ))}
              {statusFilter.length > 0 && [
                <Divider key="d" />,
                <MenuItem key="clr" onClick={() => setStatusFilter([])} dense>
                  Clear all
                </MenuItem>,
              ]}
            </Menu>
            <Typography sx={{ color: TOKENS.textMuted, fontSize: 13, ml: 1 }}>
              {loading && accumulated.length === 0
                ? "Loading…"
                : `${sorted.length} of ${enriched.length} loaded`}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Density */}
            <Box
              sx={{
                display: "inline-flex",
                border: `1px solid ${TOKENS.border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <Tooltip title="Comfortable">
                <Box
                  onClick={() => setDensity("comfortable")}
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    cursor: "pointer",
                    background: density === "comfortable" ? TOKENS.primarySoft : "transparent",
                    color: density === "comfortable" ? TOKENS.primary : TOKENS.textSoft,
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  Cozy
                </Box>
              </Tooltip>
              <Tooltip title="Compact">
                <Box
                  onClick={() => setDensity("compact")}
                  sx={{
                    px: 1.25,
                    py: 0.6,
                    cursor: "pointer",
                    background: density === "compact" ? TOKENS.primarySoft : "transparent",
                    color: density === "compact" ? TOKENS.primary : TOKENS.textSoft,
                    fontSize: 12,
                    fontWeight: 600,
                    borderLeft: `1px solid ${TOKENS.border}`,
                  }}
                >
                  Compact
                </Box>
              </Tooltip>
            </Box>
            {/* View toggle */}
            <Box
              sx={{
                display: "inline-flex",
                border: `1px solid ${TOKENS.border}`,
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <Tooltip title="Table">
                <IconButton
                  size="small"
                  onClick={() => setView("table")}
                  sx={{
                    borderRadius: 0,
                    background: view === "table" ? TOKENS.primarySoft : "transparent",
                    color: view === "table" ? TOKENS.primary : TOKENS.textSoft,
                  }}
                >
                  <ViewListIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Cards">
                <IconButton
                  size="small"
                  onClick={() => setView("cards")}
                  sx={{
                    borderRadius: 0,
                    background: view === "cards" ? TOKENS.primarySoft : "transparent",
                    color: view === "cards" ? TOKENS.primary : TOKENS.textSoft,
                    borderLeft: `1px solid ${TOKENS.border}`,
                  }}
                >
                  <ViewModuleIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            <Tooltip title="Refresh">
              <IconButton size="small" onClick={refresh}>
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Bulk action bar */}
        {selected.length > 0 && canManage && (
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 5,
              mb: 2,
              background: TOKENS.text,
              color: "#fff",
              borderRadius: "12px",
              padding: "10px 14px",
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              boxShadow: "0 4px 16px rgba(16,24,40,0.18)",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
              {selected.length} selected
            </Typography>
            <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,.2)" }} />
            <Button
              size="small"
              onClick={bulkActivate}
              startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
              sx={{ color: "#fff", textTransform: "none", "&:hover": { background: "rgba(255,255,255,.08)" } }}
            >
              Activate
            </Button>
            <Button
              size="small"
              onClick={bulkSuspend}
              startIcon={<PauseCircleOutlineIcon sx={{ fontSize: 16 }} />}
              sx={{ color: "#fff", textTransform: "none", "&:hover": { background: "rgba(255,255,255,.08)" } }}
            >
              Suspend
            </Button>
            <Button
              size="small"
              onClick={bulkRemove}
              startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
              sx={{ color: "#fff", textTransform: "none", "&:hover": { background: "rgba(255,255,255,.08)" } }}
            >
              Remove
            </Button>
            <Box flex={1} />
            <Button
              size="small"
              onClick={() => setSelected([])}
              sx={{ color: "#fff", textTransform: "none", "&:hover": { background: "rgba(255,255,255,.08)" } }}
              startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
            >
              Clear
            </Button>
          </Box>
        )}

        {/* TABLE VIEW */}
        {view === "table" && (
          <Box sx={{ overflowX: "auto", borderRadius: "12px", border: `1px solid ${TOKENS.borderSoft}` }}>
            <Box component="table" sx={{ borderCollapse: "collapse", width: "100%", minWidth: 1100 }}>
              <thead>
                <tr>
                  <HeaderCell compact={compact} style={{ width: 36 }}>
                    <Checkbox
                      size="small"
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected}
                      onChange={(e) => handleSelectAllOnPage(e.target.checked)}
                    />
                  </HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="title" label="Organisation" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact} align="right">
                    <SortHeader id="sites" label="Sites" orderBy={orderBy} order={order} onSort={handleSort} align="right" />
                  </HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="users" label="Seats" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="_plan" label="Plan" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="_status" label="Status" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact}>Admin</HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="current_period_end" label="Expiry" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact}>
                    <SortHeader id="created" label="Created" orderBy={orderBy} order={order} onSort={handleSort} />
                  </HeaderCell>
                  <HeaderCell compact={compact} style={{ width: 48 }}></HeaderCell>
                </tr>
              </thead>
              <tbody>
                {loading
                  ? Array.from({ length: 5 }).map((_, i) => (
                      <tr key={`sk-${i}`}>
                        {Array.from({ length: 10 }).map((__, j) => (
                          <DataCell key={`sk-${i}-${j}`} compact={compact}>
                            <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                          </DataCell>
                        ))}
                      </tr>
                    ))
                  : visible.length === 0
                  ? (
                    <tr>
                      <DataCell compact={compact} colSpan={10}>
                        <Stack alignItems="center" spacing={1} py={6}>
                          <Box
                            sx={{
                              width: 48,
                              height: 48,
                              borderRadius: "12px",
                              background: TOKENS.surfaceAlt,
                              border: `1px solid ${TOKENS.border}`,
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: TOKENS.textMuted,
                            }}
                          >
                            <SearchIcon sx={{ fontSize: 22 }} />
                          </Box>
                          <Typography sx={{ fontWeight: 600, color: TOKENS.text }}>
                            No organisations match
                          </Typography>
                          <Typography sx={{ color: TOKENS.textMuted, fontSize: 13 }}>
                            Try adjusting your search or filters.
                          </Typography>
                        </Stack>
                      </DataCell>
                    </tr>
                  )
                  : visible.map((org) => {
                      const isChecked = selected.includes(org.id);
                      return (
                        <Box
                          component="tr"
                          key={org.id}
                          onClick={() => handleViewDetails(org)}
                          sx={{
                            cursor: "pointer",
                            background: isChecked ? TOKENS.primarySoft : "transparent",
                            "&:hover": { background: isChecked ? TOKENS.primarySoft : TOKENS.surfaceAlt },
                          }}
                        >
                          <DataCell compact={compact} onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              size="small"
                              checked={isChecked}
                              onChange={() => toggleOne(org.id)}
                            />
                          </DataCell>
                          <DataCell compact={compact}>
                            <Stack direction="row" alignItems="center" spacing={1.25}>
                              <Monogram name={org.title} size={compact ? 32 : 36} />
                              <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 600, color: TOKENS.text, fontSize: 13.5, lineHeight: 1.3 }}>
                                  {org.title}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                    fontSize: 11,
                                    color: TOKENS.textMuted,
                                  }}
                                >
                                  {String(org.id || "").slice(0, 8)}
                                </Typography>
                              </Stack>
                            </Stack>
                          </DataCell>
                          <DataCell compact={compact} align="right">
                            <Typography sx={{ fontWeight: 600 }}>{org.sites ?? 0}</Typography>
                          </DataCell>
                          <DataCell compact={compact}>
                            <UsageBar users={Number(org.users || 0)} plan={org._plan} />
                          </DataCell>
                          <DataCell compact={compact}>
                            <PlanChip plan={org._plan} />
                          </DataCell>
                          <DataCell compact={compact}>
                            <StatusPill status={org._status} />
                          </DataCell>
                          <DataCell compact={compact}>
                            {org.admin?.email ? (
                              <Stack spacing={0.25}>
                                <Typography sx={{ fontSize: 13, color: TOKENS.text }}>
                                  {org.admin?.username || "—"}
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: TOKENS.textMuted }}>
                                  {org.admin?.email}
                                </Typography>
                              </Stack>
                            ) : (
                              <UnassignedChip />
                            )}
                          </DataCell>
                          <DataCell compact={compact}>
                            <ExpiryCell value={org.current_period_end} />
                          </DataCell>
                          <DataCell compact={compact}>
                            <CreatedCell value={org.created} />
                          </DataCell>
                          <DataCell compact={compact} onClick={(e) => e.stopPropagation()}>
                            <IconButton size="small" onClick={(e) => openKebab(e, org)}>
                              <MoreVertIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </DataCell>
                        </Box>
                      );
                    })}
                {/* loading-more skeleton rows */}
                {!loading && loadingMore &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <tr key={`load-sk-${i}`}>
                      {Array.from({ length: 10 }).map((__, j) => (
                        <DataCell key={`load-sk-${i}-${j}`} compact={compact}>
                          <Skeleton variant="rectangular" height={18} sx={{ borderRadius: 1 }} />
                        </DataCell>
                      ))}
                    </tr>
                  ))}
              </tbody>
            </Box>
          </Box>
        )}

        {/* CARD VIEW */}
        {view === "cards" && (
          <Box>
            {loading ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                  gap: 2,
                }}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} height={180} variant="rectangular" sx={{ borderRadius: 2 }} />
                ))}
              </Box>
            ) : visible.length === 0 ? (
              <Stack alignItems="center" spacing={1} py={8}>
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    background: TOKENS.surfaceAlt,
                    border: `1px solid ${TOKENS.border}`,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: TOKENS.textMuted,
                  }}
                >
                  <SearchIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography sx={{ fontWeight: 600, color: TOKENS.text }}>
                  No organisations match
                </Typography>
                <Typography sx={{ color: TOKENS.textMuted, fontSize: 13 }}>
                  Try adjusting your search or filters.
                </Typography>
              </Stack>
            ) : (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                  gap: 2,
                }}
              >
                {visible.map((org) => {
                  const isChecked = selected.includes(org.id);
                  return (
                    <Box
                      key={org.id}
                      onClick={() => handleViewDetails(org)}
                      sx={{
                        cursor: "pointer",
                        border: `1px solid ${isChecked ? TOKENS.primary : TOKENS.border}`,
                        borderRadius: "14px",
                        background: TOKENS.surface,
                        p: 2,
                        boxShadow: isChecked ? TOKENS.shadowSelected : TOKENS.shadow,
                        transition: "all .15s ease",
                        "&:hover": { borderColor: TOKENS.primary, boxShadow: TOKENS.shadowSelected },
                      }}
                    >
                      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.25}>
                        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
                          <Checkbox
                            size="small"
                            checked={isChecked}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => toggleOne(org.id)}
                          />
                          <Monogram name={org.title} size={40} />
                          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontWeight: 700,
                                fontSize: 14,
                                color: TOKENS.text,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                maxWidth: 180,
                              }}
                            >
                              {org.title}
                            </Typography>
                            <Typography
                              sx={{
                                fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                                fontSize: 11,
                                color: TOKENS.textMuted,
                              }}
                            >
                              {String(org.id || "").slice(0, 8)}
                            </Typography>
                          </Stack>
                        </Stack>
                        <IconButton size="small" onClick={(e) => openKebab(e, org)}>
                          <MoreVertIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} mb={1.5}>
                        <PlanChip plan={org._plan} />
                        <StatusPill status={org._status} />
                      </Stack>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: 1,
                          mb: 1.25,
                          p: 1.25,
                          background: TOKENS.surfaceAlt,
                          borderRadius: "10px",
                        }}
                      >
                        <Box>
                          <Typography sx={{ fontSize: 10, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
                            Sites
                          </Typography>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.text }}>
                            {org.sites ?? 0}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography sx={{ fontSize: 10, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
                            Users
                          </Typography>
                          <Typography sx={{ fontSize: 18, fontWeight: 700, color: TOKENS.text }}>
                            {org.users ?? 0}
                          </Typography>
                        </Box>
                      </Box>
                      <Box mb={1}>
                        <UsageBar users={Number(org.users || 0)} plan={org._plan} />
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        {org.admin?.email ? (
                          <Stack spacing={0}>
                            <Typography sx={{ fontSize: 12, color: TOKENS.text, fontWeight: 600 }}>
                              {org.admin?.username || "Admin"}
                            </Typography>
                            <Typography sx={{ fontSize: 11, color: TOKENS.textMuted }}>
                              {org.admin?.email}
                            </Typography>
                          </Stack>
                        ) : (
                          <UnassignedChip />
                        )}
                        <Typography sx={{ fontSize: 11, color: TOKENS.textMuted }}>
                          {org.created ? `Created ${relativeFromNow(org.created)}` : ""}
                        </Typography>
                      </Stack>
                    </Box>
                  );
                })}
                {!loading && loadingMore &&
                  Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton
                      key={`card-sk-${i}`}
                      height={180}
                      variant="rectangular"
                      sx={{ borderRadius: 2 }}
                    />
                  ))}
              </Box>
            )}
          </Box>
        )}

        <Box
          ref={sentinelRef}
          sx={{ height: 1, width: "100%", mt: 1 }}
          aria-hidden="true"
        />
        {!loading &&
          accumulated.length > 0 &&
          accumulated.length >= total_organisation_count && (
            <Box
              sx={{
                textAlign: "center",
                color: "text.disabled",
                fontSize: 12,
                py: 2,
              }}
            >
              All {total_organisation_count} organisation{total_organisation_count === 1 ? "" : "s"} loaded
            </Box>
          )}
      </Box>

      {/* Kebab menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeKebab}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem onClick={() => { closeKebab(); handleViewDetails(menuOrg); }} dense>
          <AccountBoxOutlinedIcon sx={{ fontSize: 18, mr: 1, color: TOKENS.textSoft }} />
          View details
        </MenuItem>
        {canManage && (
          <MenuItem onClick={() => handleConfigure(menuOrg)} dense>
            <SettingsIcon sx={{ fontSize: 18, mr: 1, color: TOKENS.textSoft }} />
            Configuration
          </MenuItem>
        )}
        <MenuItem onClick={() => handleUsers(menuOrg)} dense>
          <PeopleOutlineIcon sx={{ fontSize: 18, mr: 1, color: TOKENS.textSoft }} />
          Users
        </MenuItem>
        {canManage && (
          <MenuItem onClick={() => handleEdit(menuOrg)} dense>
            <EditOutlinedIcon sx={{ fontSize: 18, mr: 1, color: TOKENS.textSoft }} />
            Edit
          </MenuItem>
        )}
        {canManage && <Divider />}
        {canManage && menuOrg?.isDeleted && (
          <MenuItem onClick={() => handleRestore(menuOrg)} dense>
            <RestoreIcon sx={{ fontSize: 18, mr: 1, color: "#0f7a42" }} />
            Restore
          </MenuItem>
        )}
        {canManage && !menuOrg?.isDeleted && (
          <MenuItem onClick={() => handleSuspend(menuOrg)} dense>
            <PauseCircleOutlineIcon sx={{ fontSize: 18, mr: 1, color: "#7a5b10" }} />
            Suspend
          </MenuItem>
        )}
        {canManage && (
          <MenuItem onClick={() => handleRemove(menuOrg)} dense sx={{ color: "#A11228" }}>
            <DeleteOutlineIcon sx={{ fontSize: 18, mr: 1 }} />
            Remove
          </MenuItem>
        )}
      </Menu>

      {/* Detail drawer */}
      <Drawer
        anchor="right"
        open={Boolean(drawerOrg)}
        onClose={() => setDrawerOrg(null)}
        PaperProps={{ sx: { width: 420, p: 0 } }}
      >
        {drawerOrg && (
          <Stack sx={{ height: "100%" }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ p: 2, borderBottom: `1px solid ${TOKENS.border}` }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: 15 }}>Organisation</Typography>
              <IconButton size="small" onClick={() => setDrawerOrg(null)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
            <Box sx={{ p: 2, flex: 1, overflowY: "auto" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
                <Monogram name={drawerOrg.title} size={48} />
                <Stack spacing={0.25}>
                  <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{drawerOrg.title}</Typography>
                  <Typography
                    sx={{
                      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                      fontSize: 11,
                      color: TOKENS.textMuted,
                    }}
                  >
                    {drawerOrg.id}
                  </Typography>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={1} mb={2}>
                <PlanChip plan={drawerOrg._plan} />
                <StatusPill status={drawerOrg._status} />
              </Stack>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 1.25,
                  mb: 2,
                  p: 1.5,
                  background: TOKENS.surfaceAlt,
                  borderRadius: "12px",
                  border: `1px solid ${TOKENS.borderSoft}`,
                }}
              >
                <Box>
                  <Typography sx={{ fontSize: 10, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
                    Sites
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{drawerOrg.sites ?? 0}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>
                    Users
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: 700 }}>{drawerOrg.users ?? 0}</Typography>
                </Box>
                <Box sx={{ gridColumn: "1 / -1" }}>
                  <Typography sx={{ fontSize: 10, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.5 }}>
                    Seat usage
                  </Typography>
                  <UsageBar users={Number(drawerOrg.users || 0)} plan={drawerOrg._plan} />
                </Box>
              </Box>

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <Box>
                  <Typography sx={{ fontSize: 11, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.5 }}>
                    Admin
                  </Typography>
                  {drawerOrg.admin?.email ? (
                    <Stack spacing={0}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{drawerOrg.admin.username || "—"}</Typography>
                      <Typography sx={{ fontSize: 12, color: TOKENS.textMuted }}>{drawerOrg.admin.email}</Typography>
                    </Stack>
                  ) : (
                    <UnassignedChip />
                  )}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.5 }}>
                    Expiry
                  </Typography>
                  <ExpiryCell value={drawerOrg.current_period_end} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, color: TOKENS.textMuted, fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase", mb: 0.5 }}>
                    Created
                  </Typography>
                  <CreatedCell value={drawerOrg.created} />
                </Box>
              </Stack>
            </Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                p: 2,
                borderTop: `1px solid ${TOKENS.border}`,
                background: TOKENS.surfaceAlt,
              }}
            >
              {canManage && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => { const o = drawerOrg; setDrawerOrg(null); handleConfigure(o); }}
                  startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    background: TOKENS.primary,
                    textTransform: "none",
                    "&:hover": { background: "#5260E5" },
                  }}
                >
                  Configure
                </Button>
              )}
              <Button
                size="small"
                variant="outlined"
                onClick={() => { const o = drawerOrg; setDrawerOrg(null); handleUsers(o); }}
                startIcon={<PeopleOutlineIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: "none" }}
              >
                Users
              </Button>
              {canManage && (
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => { const o = drawerOrg; setDrawerOrg(null); handleEdit(o); }}
                  startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
                  sx={{ textTransform: "none" }}
                >
                  Edit
                </Button>
              )}
              <Box flex={1} />
              {canManage && (
                <Button
                  size="small"
                  variant="text"
                  onClick={() => { const o = drawerOrg; setDrawerOrg(null); handleRemove(o); }}
                  startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                  sx={{ color: "#A11228", textTransform: "none" }}
                >
                  Remove
                </Button>
              )}
            </Stack>
          </Stack>
        )}
      </Drawer>

      {/* Modals reused as-is */}
      <DeleteOrganisationModal
        open={deleteOpen}
        handleClose={() => setDeleteOpen(false)}
        orgID={actionIds}
        orgName={actionOrgs}
        removeFlag={removeFlag}
      />
      <RestoreOrganisationModal
        open={restoreOpen}
        handleClose={() => setRestoreOpen(false)}
        orgID={actionIds}
        orgName={actionOrgs}
      />
      <EditOrganisationModal
        open={editOpen}
        handleClose={() => setEditOpen(false)}
        orgName={actionOrgs}
      />
    </Stack>
  );
}

export default T_Organisation;
